import React, { useMemo, useState } from 'react'
import { CButton, CCard, CCardBody, CCardHeader, CCol, CRow } from '@coreui/react-pro'
import CIcon from '@coreui/icons-react'
import {
  cilArrowBottom,
  cilArrowTop,
  cilBolt,
  cilChartLine,
  cilClock,
  cilDollar,
  cilGamepad,
  cilLightbulb,
  cilMoon,
  cilPeople,
  cilPlus,
  cilUserPlus,
} from '@coreui/icons'
import { CChart } from '@coreui/react-chartjs'
import { useChartTheme, baseChartOptions, axis } from '../AnalyticsV2/chartTheme'
import {
  ChartSkeleton,
  EmptyState,
  ErrorState,
  StatCard,
  TileSkeleton,
  computeDelta,
  formatDateLabel,
  formatDecimal,
  formatNumber,
} from '../AnalyticsV2/common'
import RankedBarList from '../AnalyticsV2/RankedBarList'
import { HEALTH, cssToken, formatMoney } from './platformHelpers'

const KPI_TILES = [
  {
    key: 'active_users',
    label: 'Active app users',
    icon: cilPeople,
    color: 'primary',
    spark: true,
  },
  {
    key: 'new_app_users',
    label: 'New app users (first open)',
    icon: cilUserPlus,
    color: 'success',
    spark: true,
  },
  { key: 'sessions', label: 'App sessions', icon: cilChartLine, color: 'info', spark: true },
  { key: 'active_games', label: 'Games in use', icon: cilGamepad, color: 'success', spark: true },
  { key: 'new_members', label: 'Member joins', icon: cilUserPlus, color: 'warning', spark: true },
  { key: 'new_games', label: 'New games created', icon: cilPlus, color: 'secondary', spark: true },
  {
    key: 'activation_revenue',
    label: 'Activation revenue',
    icon: cilDollar,
    color: 'success',
    format: formatMoney,
  },
  {
    key: 'avg_session_minutes',
    label: 'Average time in app',
    icon: cilClock,
    color: 'dark',
    format: (v) => `${formatDecimal(v, 1)} min`,
  },
]

const TREND_METRICS = [
  { key: 'active_users', label: 'Active users', noun: 'active users' },
  { key: 'sessions', label: 'Sessions', noun: 'sessions' },
  { key: 'active_games', label: 'Games in use', noun: 'games in use' },
  { key: 'new_members', label: 'Member joins', noun: 'member joins' },
  { key: 'new_games', label: 'New games', noun: 'new games' },
  { key: 'new_app_users', label: 'New app users', noun: 'new app users' },
]

/* ------------------------------------------------------------------ */

const Sparkline = ({ points, color }) => {
  if (!points || points.length < 2) return null
  return (
    <CChart
      customTooltips={false}
      type="line"
      className="av2-sparkline"
      data={{
        labels: points.map((_, i) => i),
        datasets: [
          {
            data: points,
            borderColor: color,
            backgroundColor: 'transparent',
            borderWidth: 2,
            pointRadius: 0,
            tension: 0.4,
          },
        ],
      }}
      options={{
        maintainAspectRatio: false,
        responsive: true,
        plugins: { legend: { display: false }, tooltip: { enabled: false } },
        scales: { x: { display: false }, y: { display: false, beginAtZero: true } },
      }}
    />
  )
}

/** "Right now" is the one number a super admin checks first, so it leads. */
const LiveCard = ({ live, onOpenGame }) => {
  const data = live.data
  return (
    <CCard className="h-100 pa-live-card">
      <CCardBody className="d-flex flex-column">
        <div className="d-flex align-items-center gap-2 mb-2">
          <span className="pa-live-dot" aria-hidden="true" />
          <span className="small fw-semibold text-uppercase pa-live-label">Live now</span>
          {data && (
            <span className="small text-medium-emphasis ms-auto">
              last {data.window_minutes} min
            </span>
          )}
        </div>

        {live.loading && !data && <TileSkeleton height={180} />}
        {live.error && !data && (
          <div className="small text-danger">Could not load who is online.</div>
        )}

        {data && (
          <>
            <div className="pa-live-value">{formatNumber(data.online_users)}</div>
            <div className="text-medium-emphasis mb-3">
              {data.online_users === 1 ? 'member' : 'members'} in the app across{' '}
              <strong className="text-body">{formatNumber(data.online_games)}</strong>{' '}
              {data.online_games === 1 ? 'game' : 'games'}
            </div>

            {data.top_games.length > 0 ? (
              <ul className="pa-live-list mt-auto">
                {data.top_games.map((g) => (
                  <li key={g.game_id}>
                    <button
                      type="button"
                      className="pa-live-row"
                      onClick={() => onOpenGame({ id: g.game_id, game_name: g.game_name })}
                    >
                      <span className="text-truncate">{g.game_name}</span>
                      <span className="fw-semibold">{formatNumber(g.users)}</span>
                    </button>
                  </li>
                ))}
              </ul>
            ) : (
              <div className="small text-medium-emphasis mt-auto">
                Nobody is in the app right now. This refreshes every minute.
              </div>
            )}
          </>
        )}
      </CCardBody>
    </CCard>
  )
}

const KpiGrid = ({ overview, trendRows, theme, onRetry }) => {
  if (overview.loading) {
    return (
      <CRow className="g-3">
        {KPI_TILES.map((t) => (
          <CCol key={t.key} xs={12} sm={6} xl={3}>
            <TileSkeleton />
          </CCol>
        ))}
      </CRow>
    )
  }
  if (overview.error) {
    return (
      <CCard className="h-100">
        <CCardBody>
          <ErrorState message={overview.error} onRetry={onRetry} height={200} />
        </CCardBody>
      </CCard>
    )
  }

  const cur = overview.data?.current || {}
  const prev = overview.data?.previous || {}

  return (
    <CRow className="g-3">
      {KPI_TILES.map((t) => (
        <CCol key={t.key} xs={12} sm={6} xl={3}>
          <StatCard
            label={t.label}
            value={(t.format || formatNumber)(cur[t.key])}
            delta={computeDelta(cur[t.key], prev[t.key])}
            icon={t.icon}
            color={t.color}
          >
            {t.spark && (
              <Sparkline
                points={(trendRows || []).map((r) => Number(r[t.key]) || 0)}
                color={theme.series[0]}
              />
            )}
          </StatCard>
        </CCol>
      ))}
    </CRow>
  )
}

/**
 * Plain-language observations built from numbers already on the page — the
 * "so what" a busy super admin would otherwise have to work out.
 */
const buildInsights = (overview, gamesRows) => {
  const out = []
  const cur = overview?.current
  const prev = overview?.previous
  const totals = overview?.totals

  if (cur && prev) {
    const d = computeDelta(cur.active_users, prev.active_users)
    if (d.pct !== null && d.pct !== 0) {
      out.push({
        tone: d.pct > 0 ? 'success' : 'danger',
        icon: d.pct > 0 ? cilArrowTop : cilArrowBottom,
        text: (
          <>
            Active users are{' '}
            <strong>
              {d.pct > 0 ? 'up' : 'down'} {Math.abs(d.pct).toFixed(1)}%
            </strong>{' '}
            on the previous period ({formatNumber(cur.active_users)} vs{' '}
            {formatNumber(prev.active_users)}).
          </>
        ),
      })
    }
  }

  if (cur && totals?.members > 0) {
    const reach = Math.min(100, (cur.active_users / totals.members) * 100)
    out.push({
      tone: 'info',
      icon: cilPeople,
      text: (
        <>
          <strong>{reach.toFixed(0)}%</strong> of the {formatNumber(totals.members)} people in a
          game opened the app this period.
        </>
      ),
    })
  }

  if (cur?.new_app_users > 0) {
    out.push({
      tone: 'primary',
      icon: cilUserPlus,
      text: (
        <>
          <strong>{formatNumber(cur.new_app_users)}</strong> people opened the app for the first
          time — {formatNumber(cur.new_app_users_ios)} on iPhone,{' '}
          {formatNumber(cur.new_app_users_android)} on Android.
        </>
      ),
    })
  }

  if (Array.isArray(gamesRows) && gamesRows.length) {
    const withChange = gamesRows
      .filter((g) => g.health !== 'new')
      .map((g) => ({ ...g, change: g.active_users - g.previous_users }))
    const riser = withChange.reduce((best, g) => (g.change > (best?.change ?? 0) ? g : best), null)
    const faller = withChange.reduce(
      (worst, g) => (g.change < (worst?.change ?? 0) ? g : worst),
      null,
    )

    if (riser) {
      out.push({
        tone: 'success',
        icon: cilBolt,
        game: riser,
        text: (
          <>
            Biggest jump: <strong>{riser.game_name}</strong> gained {formatNumber(riser.change)}{' '}
            active {riser.change === 1 ? 'user' : 'users'}.
          </>
        ),
      })
    }
    if (faller) {
      out.push({
        tone: 'warning',
        icon: cilArrowBottom,
        game: faller,
        text: (
          <>
            Biggest drop: <strong>{faller.game_name}</strong> lost{' '}
            {formatNumber(Math.abs(faller.change))} active{' '}
            {Math.abs(faller.change) === 1 ? 'user' : 'users'}.
          </>
        ),
      })
    }

    const dormant = gamesRows.filter((g) => g.health === 'dormant').length
    if (dormant > 0) {
      out.push({
        tone: 'danger',
        icon: cilMoon,
        showGames: true,
        text: (
          <>
            <strong>{formatNumber(dormant)}</strong> running{' '}
            {dormant === 1 ? 'game has' : 'games have'} had no app activity at all — worth a nudge
            to their admins.
          </>
        ),
      })
    }
  }

  return out
}

const InsightsCard = ({ overview, games, onOpenGame, onShowGames }) => {
  const insights = useMemo(
    () => buildInsights(overview.data, games.data?.rows),
    [overview.data, games.data],
  )
  const loading = overview.loading || games.loading

  return (
    <CCard className="h-100">
      <CCardHeader className="d-flex align-items-center gap-2">
        <CIcon icon={cilLightbulb} className="text-warning" />
        <strong>What stands out</strong>
      </CCardHeader>
      <CCardBody>
        {loading && <TileSkeleton height={200} />}
        {!loading && insights.length === 0 && (
          <EmptyState
            height={200}
            title="Nothing unusual"
            hint="No notable changes between this period and the one before."
          />
        )}
        {!loading && insights.length > 0 && (
          <ul className="pa-insights">
            {insights.map((item, i) => {
              const clickable = item.game || item.showGames
              const onClick = item.game
                ? () => onOpenGame({ id: item.game.id, game_name: item.game.game_name })
                : item.showGames
                ? onShowGames
                : undefined
              const Tag = clickable ? 'button' : 'div'
              return (
                <li key={i}>
                  <Tag
                    {...(clickable ? { type: 'button', onClick } : {})}
                    className={`pa-insight${clickable ? ' pa-insight--interactive' : ''}`}
                  >
                    <span className={`pa-insight-icon av2-stat-puck--${item.tone}`}>
                      <CIcon icon={item.icon} size="sm" />
                    </span>
                    <span>{item.text}</span>
                  </Tag>
                </li>
              )
            })}
          </ul>
        )}
      </CCardBody>
    </CCard>
  )
}

/** One metric at a time against the previous period — a single y-axis, always. */
const TrendCard = ({ trends, interval, onIntervalChange, theme, onRetry }) => {
  const [metric, setMetric] = useState('active_users')
  const meta = TREND_METRICS.find((m) => m.key === metric)

  const cur = useMemo(() => trends.data?.current || [], [trends.data])
  const prev = useMemo(() => trends.data?.previous || [], [trends.data])
  const hasData = cur.some((r) => Number(r[metric]) > 0) || prev.some((r) => Number(r[metric]) > 0)

  const chartData = useMemo(
    () => ({
      labels: cur.map((r) => formatDateLabel(r.bucket)),
      datasets: [
        {
          label: 'This period',
          data: cur.map((r) => Number(r[metric]) || 0),
          borderColor: theme.series[0],
          backgroundColor: `${theme.series[0]}22`,
          fill: true,
          borderWidth: 2,
          pointRadius: 0,
          pointHoverRadius: 5,
          pointHoverBorderWidth: 2,
          pointHoverBorderColor: theme.surface,
          tension: 0.3,
        },
        {
          label: 'Previous period',
          data: cur.map((_, i) => Number(prev[i]?.[metric]) || 0),
          borderColor: theme.neutral,
          backgroundColor: theme.neutral,
          borderDash: [5, 4],
          borderWidth: 2,
          pointRadius: 0,
          pointHoverRadius: 4,
          tension: 0.3,
        },
      ],
    }),
    [cur, prev, metric, theme],
  )

  return (
    <CCard className="h-100">
      <CCardHeader className="d-flex flex-wrap align-items-center gap-2">
        <div className="me-auto">
          <strong>{meta.label} over time</strong>
          <div className="small text-medium-emphasis">
            Per {interval}, with the previous period dashed for comparison
          </div>
        </div>
        <div className="ge-segmented-tabs" role="tablist" aria-label="Interval">
          {['day', 'week'].map((key) => (
            <button
              key={key}
              type="button"
              role="tab"
              aria-selected={interval === key}
              className={`nav-link${interval === key ? ' active' : ''}`}
              onClick={() => onIntervalChange(key)}
            >
              {key === 'day' ? 'Daily' : 'Weekly'}
            </button>
          ))}
        </div>
      </CCardHeader>
      <CCardBody>
        <div className="av2-scroll-x mb-3">
          <div className="ge-segmented-tabs" role="tablist" aria-label="Metric">
            {TREND_METRICS.map((m) => (
              <button
                key={m.key}
                type="button"
                role="tab"
                aria-selected={metric === m.key}
                className={`nav-link${metric === m.key ? ' active' : ''}`}
                onClick={() => setMetric(m.key)}
              >
                {m.label}
              </button>
            ))}
          </div>
        </div>

        {trends.loading && <ChartSkeleton height={300} />}
        {!trends.loading && trends.error && (
          <ErrorState message={trends.error} onRetry={onRetry} height={300} />
        )}
        {!trends.loading && !trends.error && !hasData && (
          <EmptyState height={300} title={`No ${meta.noun} in this period`} />
        )}
        {!trends.loading && !trends.error && hasData && (
          <div className="av2-chart av2-chart--trend">
            <CChart
              customTooltips={false}
              type="line"
              data={chartData}
              options={baseChartOptions(theme, {
                plugins: {
                  tooltip: {
                    callbacks: {
                      label: (ctx) => {
                        const value = formatNumber(ctx.parsed.y)
                        if (ctx.datasetIndex === 1 && prev[ctx.dataIndex]) {
                          return ` Previous (${formatDateLabel(
                            prev[ctx.dataIndex].bucket,
                          )}): ${value}`
                        }
                        return ` This period: ${value}`
                      },
                    },
                  },
                },
                scales: {
                  x: axis(theme, { grid: { display: false }, ticks: { maxTicksLimit: 10 } }),
                  y: axis(theme, { beginAtZero: true, ticks: { precision: 0 } }),
                },
              })}
            />
          </div>
        )}
      </CCardBody>
    </CCard>
  )
}

const HealthCard = ({ games, theme, onShowGames, onRetry }) => {
  const counts = games.data?.health || {}
  const rows = HEALTH.map((h) => ({ ...h, value: counts[h.key] || 0 }))
  const total = rows.reduce((s, r) => s + r.value, 0)
  const colorFor = (h) => cssToken(h.color, theme.neutral)

  return (
    <CCard className="h-100">
      <CCardHeader className="d-flex align-items-center">
        <div className="me-auto">
          <strong>Game health</strong>
          <div className="small text-medium-emphasis">Every game, by how its app use is moving</div>
        </div>
        <CButton color="link" size="sm" className="p-0" onClick={onShowGames}>
          View games
        </CButton>
      </CCardHeader>
      <CCardBody>
        {games.loading && <ChartSkeleton height={220} />}
        {!games.loading && games.error && (
          <ErrorState message={games.error} onRetry={onRetry} height={220} />
        )}
        {!games.loading && !games.error && total === 0 && (
          <EmptyState height={220} title="No games yet" hint="Games appear here once created." />
        )}
        {!games.loading && !games.error && total > 0 && (
          <CRow className="g-3 align-items-center">
            <CCol xs={12} sm={5}>
              <div className="av2-chart pa-chart--health">
                <CChart
                  customTooltips={false}
                  type="doughnut"
                  data={{
                    labels: rows.map((r) => r.label),
                    datasets: [
                      {
                        data: rows.map((r) => r.value),
                        backgroundColor: rows.map(colorFor),
                        borderColor: theme.surface,
                        borderWidth: 2,
                        hoverBorderColor: theme.surface,
                      },
                    ],
                  }}
                  options={baseChartOptions(theme, {
                    cutout: '68%',
                    plugins: { legend: { display: false } },
                  })}
                />
                <div className="pa-donut-center" aria-hidden="true">
                  <div className="fs-4 fw-semibold lh-1">{formatNumber(total)}</div>
                  <div className="small text-medium-emphasis">games</div>
                </div>
              </div>
            </CCol>
            <CCol xs={12} sm={7}>
              <ul className="pa-legend">
                {rows.map((r) => (
                  <li key={r.key} title={r.hint}>
                    <span className="av2-swatch" style={{ background: colorFor(r) }} />
                    <span className="me-auto">{r.label}</span>
                    <span className="fw-semibold">{formatNumber(r.value)}</span>
                    <span className="text-medium-emphasis pa-legend-pct">
                      {total ? Math.round((r.value / total) * 100) : 0}%
                    </span>
                  </li>
                ))}
              </ul>
            </CCol>
          </CRow>
        )}
      </CCardBody>
    </CCard>
  )
}

/** Each stage is a subset of the one above, so the step-down is the real drop-off. */
const FunnelCard = ({ overview, onRetry }) => {
  const stages = overview.data?.funnel || []
  const top = stages[0]?.value || 0

  return (
    <CCard className="h-100">
      <CCardHeader>
        <strong>Game pipeline</strong>
        <div className="small text-medium-emphasis">From sign-up to a game people actually use</div>
      </CCardHeader>
      <CCardBody>
        {overview.loading && <ChartSkeleton height={220} />}
        {!overview.loading && overview.error && (
          <ErrorState message={overview.error} onRetry={onRetry} height={220} />
        )}
        {!overview.loading && !overview.error && (
          <ol className="pa-funnel">
            {stages.map((stage, i) => {
              const width = top ? Math.max(3, (stage.value / top) * 100) : 0
              const prevValue = i > 0 ? stages[i - 1].value : null
              const conv = prevValue ? Math.round((stage.value / prevValue) * 100) : null
              return (
                <li key={stage.key}>
                  <div className="d-flex justify-content-between small mb-1">
                    <span>{stage.label}</span>
                    <span>
                      <strong>{formatNumber(stage.value)}</strong>
                      {conv !== null && (
                        <span className="text-medium-emphasis ms-2">{conv}% of previous</span>
                      )}
                    </span>
                  </div>
                  <div className="pa-funnel-track">
                    <div
                      className="pa-funnel-bar"
                      style={{ width: `${width}%`, opacity: 1 - i * 0.18 }}
                    />
                  </div>
                </li>
              )
            })}
          </ol>
        )}
      </CCardBody>
    </CCard>
  )
}

const TopGamesTrend = ({ trends, theme, onRetry }) => {
  const series = trends.data?.top_games || []
  const labels = (series[0]?.points || []).map((p) => formatDateLabel(p.bucket))

  return (
    <CCard className="h-100">
      <CCardHeader>
        <strong>Top 5 games over time</strong>
        <div className="small text-medium-emphasis">Active users per game</div>
      </CCardHeader>
      <CCardBody>
        {trends.loading && <ChartSkeleton height={300} />}
        {!trends.loading && trends.error && (
          <ErrorState message={trends.error} onRetry={onRetry} height={300} />
        )}
        {!trends.loading && !trends.error && series.length === 0 && <EmptyState height={300} />}
        {!trends.loading && !trends.error && series.length > 0 && (
          <div className="av2-chart av2-chart--trend">
            <CChart
              customTooltips={false}
              type="line"
              data={{
                labels,
                // Fixed slot order: colour follows the rank-at-load, never cycles.
                datasets: series.map((s, i) => ({
                  label: s.game_name,
                  data: s.points.map((p) => p.value),
                  borderColor: theme.series[i],
                  backgroundColor: theme.series[i],
                  borderWidth: 2,
                  pointRadius: 0,
                  pointHoverRadius: 5,
                  pointHoverBorderWidth: 2,
                  pointHoverBorderColor: theme.surface,
                  tension: 0.3,
                })),
              }}
              options={baseChartOptions(theme, {
                scales: {
                  x: axis(theme, { grid: { display: false }, ticks: { maxTicksLimit: 8 } }),
                  y: axis(theme, { beginAtZero: true, ticks: { precision: 0 } }),
                },
              })}
            />
          </div>
        )}
      </CCardBody>
    </CCard>
  )
}

const MostActiveGames = ({ games, theme, onOpenGame, onShowGames, onRetry }) => {
  const rows = useMemo(
    () =>
      (games.data?.rows || [])
        .filter((g) => g.active_users > 0)
        .sort((a, b) => b.active_users - a.active_users)
        .slice(0, 8)
        .map((g) => {
          const d = computeDelta(g.active_users, g.previous_users)
          return {
            key: g.id,
            label: g.game_name,
            sublabel: `${g.hub_name || 'No hub'} · ${d.label}`,
            value: g.active_users,
            people: g.members,
            game: g,
          }
        }),
    [games.data],
  )

  return (
    <CCard className="h-100">
      <CCardHeader className="d-flex align-items-center">
        <div className="me-auto">
          <strong>Most active games</strong>
          <div className="small text-medium-emphasis">Active users vs total members</div>
        </div>
        <CButton color="link" size="sm" className="p-0" onClick={onShowGames}>
          All games
        </CButton>
      </CCardHeader>
      <CCardBody className="pa-ranked-plain">
        {games.loading && <ChartSkeleton height={300} />}
        {!games.loading && games.error && (
          <ErrorState message={games.error} onRetry={onRetry} height={300} />
        )}
        {!games.loading && !games.error && (
          <RankedBarList
            rows={rows}
            color={theme.series[0]}
            valueLabel="active"
            emptyLabel="No game had app activity in this period."
            onRowClick={(row) => onOpenGame({ id: row.game.id, game_name: row.game.game_name })}
          />
        )}
      </CCardBody>
    </CCard>
  )
}

const OverviewPanel = ({
  overview,
  trends,
  games,
  live,
  interval,
  onIntervalChange,
  onOpenGame,
  onShowGames,
  onRetry,
}) => {
  const theme = useChartTheme()

  return (
    <>
      <CRow className="g-3 mb-3">
        <CCol xs={12} lg={4} xxl={3}>
          <LiveCard live={live} onOpenGame={onOpenGame} />
        </CCol>
        <CCol xs={12} lg={8} xxl={9}>
          <KpiGrid
            overview={overview}
            trendRows={trends.data?.current}
            theme={theme}
            onRetry={onRetry}
          />
        </CCol>
      </CRow>

      <CRow className="g-3 mb-3">
        <CCol xs={12} xl={8}>
          <TrendCard
            trends={trends}
            interval={interval}
            onIntervalChange={onIntervalChange}
            theme={theme}
            onRetry={onRetry}
          />
        </CCol>
        <CCol xs={12} xl={4}>
          <InsightsCard
            overview={overview}
            games={games}
            onOpenGame={onOpenGame}
            onShowGames={onShowGames}
          />
        </CCol>
      </CRow>

      <CRow className="g-3 mb-3">
        <CCol xs={12} lg={6}>
          <HealthCard games={games} theme={theme} onShowGames={onShowGames} onRetry={onRetry} />
        </CCol>
        <CCol xs={12} lg={6}>
          <FunnelCard overview={overview} onRetry={onRetry} />
        </CCol>
      </CRow>

      <CRow className="g-3 mb-3">
        <CCol xs={12} xl={7}>
          <TopGamesTrend trends={trends} theme={theme} onRetry={onRetry} />
        </CCol>
        <CCol xs={12} xl={5}>
          <MostActiveGames
            games={games}
            theme={theme}
            onOpenGame={onOpenGame}
            onShowGames={onShowGames}
            onRetry={onRetry}
          />
        </CCol>
      </CRow>
    </>
  )
}

export default OverviewPanel
